import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "../../api/fddClient";
import { getFolderRef, getIndexRows, getProviderId, saveIndexRows, saveProviderId } from "../../store/folderStore";
import { loadScorpReport, saveScorpReport, type SavedScorpReport } from "../scorpStore";
import { extractCodesFromTree, fetchSharePointTree, type SPTreeNode } from "../../utils/sharePointTree";
import { getToken } from "../../utils/tokenHelper";
import { isProviderImplemented, parseWithProvider } from "../../vdr/parserBridge";
import { VDR_PROVIDERS, type IndexRow, type VDRProviderId } from "../../vdr/providers";
import ScorpReport, { type ReportData } from "./ScorpReport";

/**
 * S-Corp Diligence.
 *
 *   1. Drop the VDR file index (or reuse the one already on the project). The
 *      index says which sections should hold the documents an S-corp review
 *      needs.
 *   2. The SharePoint folder tree is shown with those folders pre-ticked, so
 *      the search is isolated to the folders the reviewer chooses.
 *   3. Files in the ticked folders are matched against the required-document
 *      list; anything missing can be dropped in, call notes included.
 *   4. Run, then read the report.
 *
 * The browser downloads the SharePoint files with the user's own token and
 * posts the bytes; the server never holds a Graph credential.
 */

type Phase = "idle" | "folders" | "coverage" | "running" | "report";

interface CatalogEntry {
  id: string;
  name: string;
  path: string;
  driveId: string;
  itemId: string;
}

interface CoverageItem {
  id: string;
  title: string;
  required: boolean;
  status: "found" | "missing";
  files: string[];
}

interface Coverage {
  items: CoverageItem[];
  unmatched: string[];
}

interface SectionHit {
  code: string;
  title: string;
  items: string[];
}

const green = "var(--theme-ink)";
const line = "var(--theme-border-strong)";
const btn = (primary = true): React.CSSProperties => ({
  background: primary ? "var(--theme-accent)" : "#fff",
  color: primary ? "#fff" : green,
  border: `1px solid ${primary ? "var(--theme-accent)" : line}`,
  borderRadius: 8,
  padding: "8px 14px",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 600,
});
const section: React.CSSProperties = { border: `1px solid ${line}`, borderRadius: 10, padding: 14, background: "#fff" };
const stepTitle: React.CSSProperties = { fontWeight: 700, color: green, marginBottom: 8 };
const hint: React.CSSProperties = { fontSize: 13, color: "var(--theme-ink-soft)", marginBottom: 8 };

async function serverError(res: Response, what: string): Promise<Error> {
  const text = await res.text();
  const json = text.trim().startsWith("{") ? (JSON.parse(text) as { error?: string }) : null;
  if (json?.error) return new Error(json.error);
  return new Error(`${what} (HTTP ${res.status})`);
}

async function fetchSharePointFile(driveId: string, itemId: string, fileName: string): Promise<File> {
  const token = await getToken();
  const url = `https://graph.microsoft.com/v1.0/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}/content`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`Could not download "${fileName}" from SharePoint (HTTP ${res.status})`);
  const blob = await res.blob();
  return new File([blob], fileName, { type: blob.type || "application/octet-stream" });
}

function collectFiles(node: SPTreeNode, path: string[], driveId: string, out: CatalogEntry[]): void {
  for (const f of node.files ?? []) out.push({ id: f.id, name: f.name, path: path.join("/"), driveId, itemId: f.id });
  for (const child of node.children) collectFiles(child, [...path, child.name], driveId, out);
}

/** A drop zone that also opens the file picker on click. */
const DropBox: React.FC<{ label: string; onFiles: (files: FileList) => void; accept?: string; wide?: boolean }> = ({ label, onFiles, accept, wide }) => {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onFiles(e.dataTransfer.files); }}
      onClick={() => input.current?.click()}
      style={{
        border: `2px dashed ${over ? "var(--theme-accent)" : line}`,
        background: over ? "var(--theme-tint-8)" : "#fff",
        borderRadius: 8,
        padding: wide ? "18px 14px" : "6px 10px",
        fontSize: wide ? 13 : 12,
        color: green,
        cursor: "pointer",
        whiteSpace: wide ? "normal" : "nowrap",
        textAlign: wide ? "center" : "left",
      }}
    >
      {label}
      <input ref={input} type="file" multiple={!accept} accept={accept} style={{ display: "none" }} onChange={(e) => { if (e.target.files) onFiles(e.target.files); e.target.value = ""; }} />
    </div>
  );
};

type Props = { projectId: string; projectName?: string };

const ScorpPanel: React.FC<Props> = ({ projectId, projectName }) => {
  const folder = useMemo(() => getFolderRef(projectId), [projectId]);

  const [phase, setPhase] = useState<Phase>("idle");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Step 1: the index.
  const [providerId, setProviderId] = useState<VDRProviderId>(() => getProviderId(projectId) ?? "none");
  const [indexRows, setIndexRows] = useState<IndexRow[]>(() => getIndexRows(projectId) ?? []);
  const [indexName, setIndexName] = useState<string>(() => (getIndexRows(projectId)?.length ? "index already on this project" : ""));
  const [sections, setSections] = useState<SectionHit[]>([]);

  // Step 2: the folders.
  const [tree, setTree] = useState<SPTreeNode | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Step 3: what the ticked folders hold.
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [added, setAdded] = useState<Array<{ forItem: string; file: File }>>([]);

  // Step 4: the run.
  const [progress, setProgress] = useState<string>("");
  const [jobId, setJobId] = useState<string | null>(null);
  const [report, setReport] = useState<ReportData | null>(null);
  const [docx, setDocx] = useState<Blob | null>(null);

  // The last report for this project, kept in this browser (../scorpStore.ts).
  const [saved, setSaved] = useState<SavedScorpReport | null>(null);
  useEffect(() => {
    let live = true;
    void loadScorpReport(projectId).then((r) => {
      if (live) setSaved(r);
    });
    return () => {
      live = false;
    };
  }, [projectId]);

  const openSaved = () => {
    if (!saved) return;
    setJobId(saved.jobId);
    setReport(saved.data);
    setDocx(saved.docx);
    setError(null);
    setPhase("report");
  };

  const byName = useMemo(() => new Map(catalog.map((c) => [c.name, c])), [catalog]);
  const itemTitle = (id: string) => coverage?.items.find((i) => i.id === id)?.title ?? id;

  // ── step 1 ────────────────────────────────────────────────────────────────
  const loadIndex = async (files: FileList) => {
    const file = files[0];
    if (!file) return;
    if (providerId === "none" || !isProviderImplemented(providerId)) {
      setError("Pick the VDR provider first, so the index can be parsed.");
      return;
    }
    setError(null);
    setBusy(`Parsing ${file.name}…`);
    try {
      const rows = await parseWithProvider(providerId, file);
      if (rows.length === 0) throw new Error(`No sections found in ${file.name}. Is the provider right?`);
      setIndexRows(rows);
      setIndexName(file.name);
      saveIndexRows(projectId, rows);
      saveProviderId(projectId, providerId);
      setTree(null);
      setCoverage(null);
      setPhase("idle");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  // ── step 2 ────────────────────────────────────────────────────────────────
  const loadFolders = async () => {
    if (!folder) {
      setError("This project has no SharePoint folder. Pick one on the project card first.");
      return;
    }
    setError(null);
    try {
      let hits: SectionHit[] = [];
      if (indexRows.length > 0) {
        setBusy(`Reading the index (${indexRows.length} sections)…`);
        const res = await apiFetch("/api/tax/scorp/folders", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ projectId, rows: indexRows.map(({ code, title, pathHint }) => ({ code, title, pathHint })) }),
        });
        if (!res.ok) throw await serverError(res, "Could not read the index");
        hits = ((await res.json()) as { rows: SectionHit[] }).rows;
      }
      setSections(hits);

      setBusy("Loading the folder tree…");
      const t = await fetchSharePointTree(folder.driveId, folder.itemId, (n) => setBusy(`Loading the folder tree… ${n} folders`));
      setTree(t);

      // Which SharePoint folders carry the index codes of interest.
      const codeToItem = indexRows.length > 0 && providerId !== "none" ? extractCodesFromTree(t, providerId, false, indexRows) : {};
      const wantedIds = new Set(hits.map((h) => codeToItem[h.code]).filter((id): id is string => !!id));
      const wantedTitles = new Set(hits.map((h) => h.title.trim().toLowerCase()));
      const wantedCodes = new Set(hits.map((h) => h.code));
      const pre = new Set<string>();
      const open = new Set<string>([t.id]);
      const walk = (n: SPTreeNode, ancestors: SPTreeNode[]) => {
        const raw = n.name.trim();
        const codeInName = raw.match(/^([\d]+(?:\.[\d]+)*)[\s_\-–.)]/)?.[1];
        const bare = raw.replace(/^[\d.]+\s*[-_–]?\s*/, "").trim().toLowerCase();
        const hit = wantedIds.has(n.id) || (codeInName !== undefined && wantedCodes.has(codeInName)) || wantedTitles.has(raw.toLowerCase()) || wantedTitles.has(bare);
        if (hit) {
          pre.add(n.id);
          for (const a of ancestors) open.add(a.id);
        }
        n.children.forEach((c) => walk(c, [...ancestors, n]));
      };
      walk(t, []);
      setChecked(pre);
      setExpanded(open);
      setPhase("folders");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const toggleChecked = (node: SPTreeNode) => {
    const ids: string[] = [];
    const walk = (n: SPTreeNode) => {
      ids.push(n.id);
      n.children.forEach(walk);
    };
    walk(node);
    setChecked((prev) => {
      const next = new Set(prev);
      const on = !prev.has(node.id);
      for (const id of ids) on ? next.add(id) : next.delete(id);
      return next;
    });
  };

  const renderNode = (node: SPTreeNode, depth: number): React.ReactNode => (
    <div key={node.id}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, paddingLeft: depth * 18, fontSize: 13 }}>
        <button
          onClick={() => setExpanded((p) => { const n = new Set(p); n.has(node.id) ? n.delete(node.id) : n.add(node.id); return n; })}
          style={{ ...btn(false), padding: "0 6px", fontSize: 11, visibility: node.children.length ? "visible" : "hidden" }}
        >
          {expanded.has(node.id) ? "–" : "+"}
        </button>
        <label style={{ cursor: "pointer" }}>
          <input type="checkbox" checked={checked.has(node.id)} onChange={() => toggleChecked(node)} /> {node.name}
        </label>
      </div>
      {expanded.has(node.id) && node.children.map((c) => renderNode(c, depth + 1))}
    </div>
  );

  // ── step 3 ────────────────────────────────────────────────────────────────
  const findDocuments = async () => {
    if (!folder || !tree) return;
    const tops: SPTreeNode[] = [];
    const walk = (n: SPTreeNode, parentChecked: boolean) => {
      const on = checked.has(n.id);
      if (on && !parentChecked) tops.push(n);
      n.children.forEach((c) => walk(c, on || parentChecked));
    };
    walk(tree, false);
    if (tops.length === 0) {
      setError("Tick at least one folder to search.");
      return;
    }
    setError(null);
    setBusy(`Listing files in ${tops.length} folder${tops.length === 1 ? "" : "s"}…`);
    try {
      const subtrees = await Promise.all(tops.map((t) => fetchSharePointTree(folder.driveId, t.id, undefined, true)));
      const files: CatalogEntry[] = [];
      subtrees.forEach((st, i) => collectFiles(st, [tops[i].name], folder.driveId, files));
      setCatalog(files);
      setBusy(`Matching ${files.length} files against the S-corp document list…`);
      const res = await apiFetch("/api/tax/scorp/coverage", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId, files: files.map(({ name, path }) => ({ name, path })) }),
      });
      if (!res.ok) throw await serverError(res, "Could not match the files");
      const cov = (await res.json()) as Coverage;
      setCoverage(cov);
      setSelected(new Set(cov.items.flatMap((i) => i.files)));
      setPhase("coverage");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const addFiles = (forItem: string, list: FileList) => {
    const incoming = Array.from(list);
    setAdded((prev) => [
      ...prev,
      ...incoming.filter((f) => !prev.some((p) => p.file.name === f.name && p.file.size === f.size)).map((file) => ({ forItem, file })),
    ]);
  };

  // ── step 4 ────────────────────────────────────────────────────────────────
  const run = async () => {
    const names = [...selected];
    if (names.length === 0 && added.length === 0) {
      setError("Nothing selected to read.");
      return;
    }
    setError(null);
    setPhase("running");
    setReport(null);
    setDocx(null);
    try {
      const body = new FormData();
      body.append("projectId", projectId);
      const paths: string[] = [];
      for (const [i, name] of names.entries()) {
        const entry = byName.get(name);
        if (!entry) continue;
        setProgress(`Downloading "${name}" (${i + 1} of ${names.length})…`);
        const file = await fetchSharePointFile(entry.driveId, entry.itemId, entry.name);
        body.append("documents", file, entry.name);
        paths.push(entry.path ? `${entry.path}/${entry.name}` : entry.name);
      }
      for (const a of added) {
        body.append("documents", a.file, a.file.name);
        paths.push(a.forItem ? `${a.forItem}/${a.file.name}` : a.file.name);
      }
      body.append("paths", JSON.stringify(paths));
      setProgress("Starting…");
      const res = await apiFetch("/api/tax/scorp/start", { method: "POST", body });
      if (!res.ok) throw await serverError(res, "Could not start");
      const { jobId: id } = (await res.json()) as { jobId: string };
      setJobId(id);
      const pid = encodeURIComponent(projectId);
      const started = Date.now();
      for (;;) {
        // 3s, and never a sign-in popup from a background poll.
        await new Promise((r) => setTimeout(r, 3000));
        const s = await apiFetch(`/api/tax/scorp/jobs/${id}?projectId=${pid}`, {}, { interactive: false });
        if (!s.ok) throw await serverError(s, "Lost track of the job");
        const status = (await s.json()) as {
          status: string;
          progress?: string;
          error?: string;
          summary?: { facts: number; exceptions: number; events: number };
        };
        setProgress(`[${Math.round((Date.now() - started) / 1000)}s] ${status.progress ?? "Working…"}`);
        if (status.status === "error") throw new Error(status.error ?? "The run failed");
        if (status.status === "done") break;
      }
      const r = await apiFetch(`/api/tax/scorp/jobs/${id}/result?projectId=${pid}`);
      if (!r.ok) throw await serverError(r, "The result has gone");
      const data = (await r.json()) as ReportData;
      // The Word document is fetched now and kept with the report, so it can
      // still be downloaded after the server has cleared the run.
      let word: Blob | null = null;
      try {
        const d = await apiFetch(`/api/tax/scorp/jobs/${id}/docx?projectId=${pid}`);
        if (d.ok) word = await d.blob();
      } catch {
        word = null;
      }
      setReport(data);
      setDocx(word);
      setPhase("report");
      const record: SavedScorpReport = { projectId, jobId: id, savedAt: new Date().toISOString(), data, docx: word };
      await saveScorpReport(record);
      setSaved(record);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPhase("coverage");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: 8, padding: "8px 12px", fontSize: 13 }}>{error}</div>}

      {saved && phase !== "report" && phase !== "running" && (
        <section style={{ ...section, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 240, fontSize: 13, color: "var(--theme-ink-soft)" }}>
            The last report for this project was saved in this browser on{" "}
            {new Date(saved.savedAt).toLocaleString()}. It is cleared when you sign out.
          </div>
          <button style={btn()} onClick={openSaved}>Open the last report</button>
        </section>
      )}

      {/* Step 1: the index */}
      <section style={section}>
        <div style={stepTitle}>1. VDR file index</div>
        <div style={hint}>
          The index says which sections should hold the documents an S-corp review needs. Pick the provider, then drop the index export here.
          {folder ? ` Project folder: ${folder.name || folder.webUrl}.` : " This project has no SharePoint folder yet; pick one on the project card."}
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <select
            value={providerId}
            onChange={(e) => setProviderId(e.target.value as VDRProviderId)}
            style={{ padding: "8px 10px", border: `1px solid ${line}`, borderRadius: 8, fontSize: 13, background: "#fff" }}
          >
            {VDR_PROVIDERS.map((p) => (
              <option key={p.id} value={p.id} disabled={p.id !== "none" && !isProviderImplemented(p.id)}>{p.label}</option>
            ))}
          </select>
          <div style={{ flex: 1, minWidth: 260 }}>
            <DropBox wide label={indexName ? `Index: ${indexName} (${indexRows.length} sections). Drop another to replace it.` : "Drop the VDR index here, or click to choose it (CSV or XLSX)"} onFiles={loadIndex} accept=".csv,.xls,.xlsx" />
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
          <button style={btn()} onClick={loadFolders} disabled={!!busy || phase === "running" || !folder}>
            {tree ? "Reload the folders" : indexRows.length ? "Find the sections of interest and load the folders" : "Load the folders without an index"}
          </button>
          {busy && <span style={{ fontSize: 13, color: "var(--theme-ink-soft)" }}>{busy}</span>}
        </div>
        {sections.length > 0 && tree && (
          <div style={{ marginTop: 10, fontSize: 12, color: "var(--theme-ink-soft)" }}>
            Sections of interest: {sections.map((s) => `${s.code} ${s.title}`).join("; ")}
          </div>
        )}
      </section>

      {/* Step 2: isolate the folders */}
      {tree && (phase === "folders" || phase === "coverage") && (
        <section style={section}>
          <div style={stepTitle}>2. Folders to search</div>
          <div style={hint}>Pre-ticked from the index. Ticking a folder includes everything under it; untick anything you do not want read.</div>
          <div style={{ maxHeight: 320, overflow: "auto", border: `1px solid ${line}`, borderRadius: 8, padding: 8 }}>{renderNode(tree, 0)}</div>
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 10 }}>
            <button style={btn()} onClick={findDocuments} disabled={!!busy || checked.size === 0}>Find the documents in the ticked folders</button>
            <span style={{ fontSize: 13, color: "var(--theme-ink-soft)" }}>{checked.size} folder(s) ticked</span>
          </div>
        </section>
      )}

      {/* Step 3: what was found, what is missing */}
      {coverage && phase === "coverage" && (
        <section style={section}>
          <div style={stepTitle}>3. Documents an S-corp review needs</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--theme-ink-soft)" }}>
                <th style={{ padding: "4px 6px" }}>Document</th>
                <th style={{ padding: "4px 6px" }}>Status</th>
                <th style={{ padding: "4px 6px" }}>Files to read</th>
                <th style={{ padding: "4px 6px" }}>Add</th>
              </tr>
            </thead>
            <tbody>
              {coverage.items.map((item) => {
                const extra = added.filter((a) => a.forItem === item.id);
                const status = item.status === "found" || extra.length ? "found" : "missing";
                return (
                  <tr key={item.id} style={{ borderTop: `1px solid ${line}` }}>
                    <td style={{ padding: "6px", verticalAlign: "top" }}>{item.title}{item.required ? "" : <span style={{ color: "#6b7280" }}> (optional)</span>}</td>
                    <td style={{ padding: "6px", verticalAlign: "top", color: status === "found" ? green : item.required ? "#b45309" : "#6b7280", fontWeight: 600 }}>{status}</td>
                    <td style={{ padding: "6px", verticalAlign: "top" }}>
                      {item.files.map((f) => (
                        <label key={f} style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 420 }} title={f}>
                          <input type="checkbox" checked={selected.has(f)} onChange={() => setSelected((p) => { const n = new Set(p); n.has(f) ? n.delete(f) : n.add(f); return n; })} /> {f}
                        </label>
                      ))}
                      {extra.map((a) => (
                        <div key={a.file.name} style={{ color: green }}>
                          + {a.file.name} <button style={{ ...btn(false), padding: "0 6px", fontSize: 11 }} onClick={() => setAdded((p) => p.filter((x) => x !== a))}>remove</button>
                        </div>
                      ))}
                    </td>
                    <td style={{ padding: "6px", verticalAlign: "top" }}><DropBox label="Drop or choose" onFiles={(fl) => addFiles(item.id, fl)} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
            <DropBox label="Drop call notes or any other supporting material" onFiles={(fl) => addFiles("", fl)} />
            {added.filter((a) => !a.forItem).map((a) => (
              <span key={a.file.name} style={{ fontSize: 12, color: green }}>+ {a.file.name}</span>
            ))}
          </div>
          {coverage.unmatched.length > 0 && (
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>In the ticked folders but not matched to any document type: {coverage.unmatched.join("; ")}</div>
          )}
          <div style={{ marginTop: 14, display: "flex", gap: 12, alignItems: "center" }}>
            <button style={btn()} onClick={run} disabled={selected.size + added.length === 0}>4. Run the S-corp review</button>
            <span style={{ fontSize: 13, color: "var(--theme-ink-soft)" }}>
              {selected.size} data room file(s) + {added.length} added{added.length ? ` (${[...new Set(added.map((a) => (a.forItem ? itemTitle(a.forItem) : "other")))].join(", ")})` : ""}
            </span>
          </div>
        </section>
      )}

      {phase === "running" && (
        <section style={{ ...section, fontFamily: "Consolas, monospace", fontSize: 13, whiteSpace: "pre-wrap" }}>{progress}</section>
      )}

      {phase === "report" && jobId && report && (
        <ScorpReport
          data={report}
          title={projectName ?? "Target"}
          jobId={jobId}
          projectId={projectId}
          docx={docx}
          onBack={() => setPhase(coverage ? "coverage" : "idle")}
        />
      )}
    </div>
  );
};

export default ScorpPanel;
